<template>
  <div v-if="selectedReport">
    <Button
      class="font-bold!"
      :class="props.classes"
      @click="triggerReport"
      outlined
      :icon="props.icon ? 'pi pi-' + props.icon : ''"
      :label="props.label"
      :disabled
    ></Button>
    <TSDialog v-model:visible="launching" :label="launchTitle()" size="w-[900px]!" icon="chart-line">
      <template #content>
        <ReportLauncher
          ref="reportLauncher"
          v-model:report="selectedReport"
          :projectId="projectId"
          :drawId="drawId"
          :checkingAccountId="checkingAccountId"
          :all-tags
          :templates
          @customizedTemplates="templatesCustomized"
        ></ReportLauncher>
      </template>
    </TSDialog>
  </div>
</template>

<script setup lang="ts">
import { CodeTableNumber } from "@/enums/CodeTable";
import { ErrorCodes } from "@/enums/ErrorCodes";
import { getCodeTable } from "@/services/Utility";
import type { Code } from "@/types/Codes";
import type { ReportTemplate, ReportData, ReportTemplateList } from "@/types/Reports";

const launching = ref<Boolean>(false);
const selectedReport = ref<ReportData>();
const availableReports = ref<ReportData[]>();
const allTags = ref<Code[]>([]);
const templates = ref<ReportTemplate[]>([]);
const reportLauncher = ref();

interface Props {
  reportId: string;
  label: string;
  projectId?: string;
  drawId?: string;
  checkingAccountId?: string;
  classes?: string;
  icon?: string;
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  classes: "w-72 h-10",
  disabled: false,
});

onMounted(async (): Promise<void> => {
  getReportData(props.reportId);
  getCodeTable(CodeTableNumber.REPORT_TAGS).then((result: Code[]) => (allTags.value = result));
});

const triggerReport = async (): Promise<void> => {
  await getTemplates();
  launching.value = true;
};

const templatesCustomized = async (): Promise<void> => {
  await getTemplates();
  if (selectedReport.value != null) {
    let defaultStillExists: boolean = false;
    templates.value.forEach((template: ReportTemplate) => {
      if (template.name === selectedReport.value?.defaultTemplate) defaultStillExists = true;
    });

    if (!defaultStillExists) selectedReport.value.defaultTemplate = "<Default Template>";
  }
  reportLauncher.value.selectDefault();
};

const getTemplates = async (): Promise<void> => {
  await axios
    .get("/reports/templates/" + props.reportId)
    .then((response) => {
      handleApiResponse(
        response,
        (templateList: ReportTemplateList) => {
          templates.value = templateList.templates;
        },
        (errors: ApiError[]) => {
          if (errors[0].code !== 3101) error("Error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const getReportData = (reportId: string): void => {
  selectedReport.value = undefined;
  availableReports.value = undefined;

  axios
    .get("/reports/single/" + reportId)
    .then((response) => {
      handleApiResponse(
        response,
        (result: ReportData) => {
          selectedReport.value = result;
        },
        (errors: ApiError[]) => {
          for (let e of errors) {
            if (e.code !== ErrorCodes.USER_DOES_NOT_HAVE_PERMISSION) error("Could not get report for id '" + reportId + "'", errors[0].message);
          }
        }
      );
    })
    .catch((e) => errorApi(e));
};

const launchTitle = (): string => {
  return selectedReport.value !== undefined ? "Launch " + selectedReport.value?.name : "Launch Report";
};
</script>
